import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import usePageLoaded from '../../hooks/usePageLoaded'
import { useShowBackButton } from '../../hooks/useBackButton'

import Button from '../../components/ui/button'
import PageHeader from '../../components/profile/pageHeader'
import UploadPhotoPopup from '../../components/profile/popups/uploadPhotoPopup'
import Album from '../../components/profile/materials/album'

import { Helmet } from 'react-helmet'
import { META } from '../../constants/meta'

import { RootState } from '../../store/rootReducer'
import { AlbumType, PhotoType } from '../../store/commonTypes'

import isEmpty from 'lodash/isEmpty'
import { checkAuth, loadMemberProfile } from '../../utils/auth'
import { fetchProfile } from '../../store/profile/profileSlice'
import { getLangFromPath, getPrefixFromPath } from '../../i18next'
import paths from '../../paths'
import { addLocationToString } from '../../utils/meta'

const ProfileAlbums: React.FunctionComponent = () => {
    usePageLoaded()
    useShowBackButton()

    const location = useLocation()
    const [t] = useTranslation()

    const self = location.pathname.includes('/membre')
    const [showPopup, setShowPopup] = useState(false)

    const { userPhotos, user } = useSelector((state: RootState) => (self ? state.member : state.profile))

    const albums: AlbumType[] = [
        {
            id: 'free',
            title: t('Public album'),
            list: [...userPhotos.filter((photo: PhotoType) => photo.type === 'free')]
        },
        {
            id: 'pro',
            title: t('Premium album'),
            list: [...userPhotos.filter((photo: PhotoType) => photo.type === 'pro')]
        },
        {
            id: 'private',
            title: t('Private album'),
            list: [...userPhotos.filter((photo: PhotoType) => photo.type === 'private')]
        }
    ]

    return (
        <div className="pageContent" style={{ margin: '0 auto', maxWidth: '1360px', padding: '90px 27px' }}>
            <Helmet>
                <title>
                    {self
                        ? META.myAlbums[getLangFromPath(location.pathname)].title
                        : addLocationToString(
                              META.userAlbums[getLangFromPath(location.pathname)].title.replace('{NICK}', user.nick),
                              user
                          )}
                </title>
                <meta
                    name="description"
                    content={
                        self
                            ? META.myAlbums[getLangFromPath(location.pathname)].description
                            : addLocationToString(
                                  META.userAlbums[getLangFromPath(location.pathname)].description.replace(
                                      '{NICK}',
                                      user.nick
                                  ),
                                  user
                              )
                    }
                />
            </Helmet>
            <div className="pageRow">
                <PageHeader user={user} self={self} text={'My albums'} />
                {self && <Button label={t('Upload photo')} small onClick={() => setShowPopup(true)} />}
            </div>

            {albums.map(
                (album: AlbumType) =>
                    !isEmpty(album.list) && <Album album={album} self={self} key={album.id} type={album.id} />
            )}

            {showPopup && <UploadPhotoPopup onClose={() => setShowPopup(false)} />}
        </div>
    )
}

// @ts-ignore
ProfileAlbums.getInitialProps = async ({ req, store, match }) => {
    const self = match.path.includes('/membre')

    if (self) {
        await checkAuth(store, true)
        const {
            member: { loggedIn }
        } = store.getState()

        if (loggedIn) {
            await loadMemberProfile(store)
        } else {
            if (!loggedIn)
                return {
                    redirectTo: `${getPrefixFromPath(req.url)}/login_required?returnPath=${paths.myAlbum.path}`
                }
        }
        // @ts-ignore
        return
    }

    const { profile } = store.getState()
    const { userContributions } = profile
    const initialNick = match.url.split('/').reverse()[1]
    if (isEmpty(userContributions)) {
        await store.dispatch(fetchProfile({ nick: initialNick }))
        const {
            profile: { user }
        } = store.getState()
        if (!user) return { statusCode: 404 }
    }
}

export default ProfileAlbums
